import type { ReactNode } from 'react'
import cx from 'classnames'
import { Button, RawButton } from '@/components/Button'
import { Dropdown } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { VersionChip } from '@/features/legends/components/LegendStatus'
import type { LegendStatus } from '@/features/legends/legend-status'
import styles from './VersionControls.module.css'

export interface VersionControlsProps {
  version: number
  status: LegendStatus
  isDraft: boolean
  isPickerOpen: boolean
  onTogglePicker: () => unknown
  onClosePicker: () => unknown
  /**
   * The version picker.
   */
  picker: ReactNode
  canDiscard: boolean
  isDiscardPending: boolean
  onDiscard: () => unknown
  testId?: string
}

/**
 * The selected version, which opens the version picker, and the button that
 * discards the changes made in this session.
 */
export function VersionControls({
  version,
  status,
  isDraft,
  isPickerOpen,
  onTogglePicker,
  onClosePicker,
  picker,
  canDiscard,
  isDiscardPending,
  onDiscard,
  testId,
}: VersionControlsProps) {
  return (
    <>
      <Dropdown
        isOpen={isPickerOpen}
        onButtonPress={onTogglePicker}
        onRequestClose={onClosePicker}
        position="bottom-start"
        button={
          <RawButton
            className={cx(styles.versionButton, isPickerOpen && styles.open)}
            title="Version history"
            testId={testId && `${testId}-version-button`}
          >
            <VersionChip version={version} status={status} isDraft={isDraft} />
            <Icon name="chevron-down" size={13} />
          </RawButton>
        }
      >
        {picker}
      </Dropdown>
      {canDiscard && (
        <Button
          label="Discard changes"
          size="small"
          iconLeft="undo"
          isPending={isDiscardPending}
          onPress={onDiscard}
          title="Discards changes made in this session. If you exit, the current changes won't be undoable."
          testId={testId && `${testId}-discard-button`}
        />
      )}
    </>
  )
}
